import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { 
  BarChart3, TrendingUp, AlertTriangle, CheckCircle2, 
  FileText, Users, Target, Download, Grid3x3, Table,
  Settings, Play, Check, X, Bell, Plus, Clock, MapPin, Eye
} from 'lucide-react';
import { toast } from 'sonner';
import { axiosInstance } from '@/App';
import AllAuditsTable from '@/components/AllAuditsTable';
import ViewAuditDialog from '@/components/ViewAuditDialog';
import CorrectiveActionWorkflow from '@/components/CorrectiveActionWorkflow';
import CorrectiveActionMetrics from '@/components/CorrectiveActionMetrics';
import useFeatureFlags from '@/hooks/useFeatureFlags';
import { useTranslation } from 'react-i18next';

const CompanyAuditDashboard = ({ user }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const flags = useFeatureFlags();
  const [metricsRefresh, setMetricsRefresh] = useState(0);
  
  // Check authorization
  useEffect(() => {
    if (user?.role !== 'company_admin' && user?.role !== 'super_admin') {
      toast.error(t('adminToasts.CompanyAdminAccessRequired'));
      navigate('/');
    }
  }, [user, navigate]);
  
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState(30); // Default: last 30 days
  
  // Data state
  const [summary, setSummary] = useState(null);
  const [recentAudits, setRecentAudits] = useState([]);
  const [heatmapData, setHeatmapData] = useState([]);
  const [repeatedHazards, setRepeatedHazards] = useState([]);
  const [correctiveActions, setCorrectiveActions] = useState([]);
  const [supervisorPerformance, setSupervisorPerformance] = useState([]);
  const [jsaIntegration, setJsaIntegration] = useState(null);
  const [aiRecommendations, setAiRecommendations] = useState([]);
  
  // Selected audit for drawer
  const [selectedAudit, setSelectedAudit] = useState(null);
  const [showAuditDrawer, setShowAuditDrawer] = useState(false);
  
  // Corrective Action Dialog State
  const [showActionDialog, setShowActionDialog] = useState(false);
  const [selectedAction, setSelectedAction] = useState(null);
  const [assignees, setAssignees] = useState([]);
  const [actionUpdate, setActionUpdate] = useState({
    status: '',
    assignedTo: '',
    dueDate: '',
    notes: '',
    priority: ''
  });
  const [savingAction, setSavingAction] = useState(false);
  
  // Hazard Details Dialog
  const [showHazardDialog, setShowHazardDialog] = useState(false);
  const [selectedHazard, setSelectedHazard] = useState(null);
  const [hazardDetails, setHazardDetails] = useState(null);
  const [loadingHazard, setLoadingHazard] = useState(false);
  
  // AI Recommendation Dialog
  const [showRecDialog, setShowRecDialog] = useState(false);
  const [selectedRec, setSelectedRec] = useState(null);
  const [actioningRec, setActioningRec] = useState(false);
  
  // Load all data
  useEffect(() => {
    if (user) {
      loadDashboardData();
      loadAssignees();
    }
  }, [dateRange, user]);
  
  // Load potential assignees
  const loadAssignees = async () => {
    try {
      const res = await axiosInstance.get('/company/corrective-actions/assignees');
      setAssignees(res.data.assignees || []);
    } catch (error) {
      console.error('Failed to load assignees:', error);
    }
  };
  
  const loadDashboardData = async () => {
    try {
      console.log('📊 Loading Company Audit Dashboard data...');
      setLoading(true);
      
      // Load all endpoints in parallel with individual error handling
      const results = await Promise.allSettled([
        axiosInstance.get(`/company/audits/summary?days=${dateRange}`),
        axiosInstance.get(`/safety-audits?limit=20`),
        axiosInstance.get(`/company/audits/heatmap?days=${dateRange}`),
        axiosInstance.get(`/company/audits/hazards/trends?days=${dateRange}`),
        axiosInstance.get(`/company/corrective-actions?days=${dateRange}`),
        axiosInstance.get(`/company/audits/supervisors?days=${dateRange}`),
        axiosInstance.get(`/company/jsa/audit-links`),
        axiosInstance.get(`/company/audits/ai-recommendations?days=${dateRange}`)
      ]);
      
      console.log('✅ API calls completed:', results.map(r => r.status));
      
      // Safely extract data from settled promises
      const summaryData = results[0].status === 'fulfilled' ? results[0].value.data : { totalAudits: 0, highRisk: 0, openActions: 0, companySafetyScore: 50 };
      console.log('📊 Summary data:', summaryData);
      
      setSummary(summaryData);
      setRecentAudits(results[1].status === 'fulfilled' ? (results[1].value.data || []).slice(0, 10) : []);
      setHeatmapData(results[2].status === 'fulfilled' ? (results[2].value.data?.heatmap || []) : []);
      setRepeatedHazards(results[3].status === 'fulfilled' ? (results[3].value.data?.repeatedHazards || []) : []);
      setCorrectiveActions(results[4].status === 'fulfilled' ? (results[4].value.data?.correctiveActions || []) : []);
      setSupervisorPerformance(results[5].status === 'fulfilled' ? (results[5].value.data?.supervisors || []) : []);
      setJsaIntegration(results[6].status === 'fulfilled' && results[6].value?.data?.jsaIntegration ? results[6].value.data.jsaIntegration : { audits_with_jsa_suggestions: 0, recent_suggestions: [] });
      setAiRecommendations(results[7].status === 'fulfilled' ? (results[7].value.data?.recommendations || []) : []);
      
      setMetricsRefresh((n) => n + 1);
      console.log('✅ Dashboard data loaded successfully');
      setLoading(false);
    } catch (error) {
      console.error('❌ Failed to load dashboard data:', error);
      toast.error(t('adminToasts.FailedToLoadDashboardData'));
      setLoading(false);
    }
  };
  
  const handleAuditClick = async (audit) => {
    // Load full audit details
    try {
      const res = await axiosInstance.get(`/safety-audits/${audit.id}`);
      setSelectedAudit(res.data);
      setShowAuditDrawer(true);
    } catch (error) {
      console.error('Failed to load audit:', error);
      toast.error(t('adminToasts.FailedToLoadAuditDetails'));
    }
  };
  
  // Open Corrective Action Dialog
  const openActionDialog = (action) => {
    setSelectedAction(action);
    setActionUpdate({
      status: action.status || 'open',
      assignedTo: action.assignedTo || '',
      dueDate: action.dueDate || '',
      notes: '',
      priority: action.priority || 'Medium'
    });
    setShowActionDialog(true);
  };
  
  // Save Corrective Action Update
  const saveActionUpdate = async () => {
    if (!selectedAction) return;
    
    setSavingAction(true);
    try {
      // Check if this is a new action from audit that needs to be converted first
      if (selectedAction.isFromAudit) {
        // First convert to tracked action
        const parts = selectedAction.id.split('_');
        const auditId = parts[1];
        const actionIndex = parseInt(parts[2]);
        
        const convertRes = await axiosInstance.post(`/company/corrective-actions/from-audit?audit_id=${auditId}&action_index=${actionIndex}`);
        const newActionId = convertRes.data.id;
        
        // Then update with the user's changes
        await axiosInstance.put(`/company/corrective-actions/${newActionId}`, actionUpdate);
        toast.success(t('adminToasts.CorrectiveActionCreatedAndUpdated'));
      } else {
        // Update existing action. The status is only sent when the user changed it, so pressing
        // Save never walks a verified or awaiting-verification action back to an earlier state.
        const payload = { ...actionUpdate };
        if (payload.status === selectedAction.status) delete payload.status;
        await axiosInstance.put(`/company/corrective-actions/${selectedAction.id}`, payload);
        toast.success(t('adminToasts.CorrectiveActionUpdated'));
      }
      
      setShowActionDialog(false);
      loadDashboardData(); // Refresh data
    } catch (error) {
      console.error('Failed to update action:', error);
      toast.error(error.response?.data?.detail || 'Failed to update corrective action');
    } finally {
      setSavingAction(false);
    }
  };
  
  // Open Hazard Details Dialog
  const openHazardDialog = async (hazard) => {
    setSelectedHazard(hazard);
    setShowHazardDialog(true);
    setLoadingHazard(true);
    
    try {
      const encodedHazard = encodeURIComponent(hazard.hazard);
      const res = await axiosInstance.get(`/company/audits/hazards/${encodedHazard}/details?days=${dateRange}`);
      setHazardDetails(res.data);
    } catch (error) {
      console.error('Failed to load hazard details:', error);
      toast.error(t('adminToasts.FailedToLoadHazardDetails'));
    } finally {
      setLoadingHazard(false);
    }
  };
  
  // Create Action from Hazard
  const createActionFromHazard = async (notifySupervisors = false) => {
    if (!selectedHazard) return;
    
    try {
      const res = await axiosInstance.post(`/company/audits/hazards/create-action?hazard=${encodeURIComponent(selectedHazard.hazard)}&notify_supervisors=${notifySupervisors}`);
      toast.success(`Action created${notifySupervisors ? ` - ${res.data.notificationsSent} supervisors notified` : ''}`);
      setShowHazardDialog(false);
      loadDashboardData();
    } catch (error) {
      console.error('Failed to create action:', error);
      toast.error(t('adminToasts.FailedToCreateAction'));
    }
  };
  
  // Handle AI Recommendation Action
  const handleRecAction = async (actionType) => {
    if (!selectedRec) return;
    
    setActioningRec(true);
    try {
      const res = await axiosInstance.post(`/company/ai-recommendations/${selectedRec.id}/action?action_type=${actionType}`);
      
      if (actionType === 'acknowledge') {
        toast.success(t('adminToasts.RecommendationAcknowledged'));
      } else if (actionType === 'dismiss') {
        toast.success(t('adminToasts.RecommendationDismissed'));
      } else if (actionType === 'create_task') {
        toast.success(t('adminToasts.TaskCreatedFromRecommendation'));
      }
      
      setShowRecDialog(false);
      loadDashboardData();
    } catch (error) {
      console.error('Failed to action recommendation:', error);
      toast.error(t('adminToasts.FailedToProcessRecommendation'));
    } finally {
      setActioningRec(false);
    }
  };
  
  // Status badge helper
  const getStatusBadge = (status) => {
    const styles = {
      open: 'bg-blue-500',
      assigned: 'bg-blue-600',
      in_progress: 'bg-yellow-500',
      pending_verification: 'bg-purple-500',
      closed: 'bg-green-600',
      completed: 'bg-green-600',
      dismissed: '',
    };
    const labels = {
      open: 'statusOpen',
      assigned: 'statusAssigned',
      in_progress: 'statusInProgress',
      pending_verification: 'statusPendingVerification',
      closed: 'statusClosed',
      completed: 'statusClosed',
      dismissed: 'statusDismissed',
    };
    const key = labels[status];
    if (!key) return <Badge variant="outline">{status}</Badge>;
    return (
      <Badge className={styles[status]} variant={styles[status] ? undefined : 'outline'}>
        {t(`correctiveWorkflow.${key}`)}
      </Badge>
    );
  };
  
  // Priority badge helper
  const getPriorityBadge = (priority) => {
    switch (priority?.toLowerCase()) {
      case 'high':
        return <Badge className="bg-red-100 text-red-800 border-red-300">{t('correctiveWorkflow.priorityHigh')}</Badge>;
      case 'medium':
        return <Badge className="bg-yellow-100 text-yellow-800 border-yellow-300">{t('correctiveWorkflow.priorityMedium')}</Badge>;
      case 'low':
        return <Badge className="bg-green-100 text-green-800 border-green-300">{t('correctiveWorkflow.priorityLow')}</Badge>;
      default:
        return <Badge variant="outline">{priority}</Badge>;
    }
  };
  
  const getRiskColor = (risk) => {
    // Ensure risk is a string
    const riskStr = typeof risk === 'string' ? risk : String(risk || '');
    const r = riskStr.toLowerCase();
    if (r === 'high') return 'bg-red-500';
    if (r === 'medium') return 'bg-yellow-500';
    if (r === 'low') return 'bg-green-500';
    return 'bg-gray-300';
  };
  
  const getRiskBadge = (risk) => {
    // Ensure risk is a string
    const riskStr = typeof risk === 'string' ? risk : String(risk || '');
    const r = riskStr.toLowerCase();
    if (r === 'critical') return <Badge className="bg-red-700">Critical</Badge>;
    if (r === 'high') return <Badge className="bg-red-500">High</Badge>;
    if (r === 'moderate' || r === 'medium') return <Badge className="bg-yellow-500">Moderate</Badge>;
    if (r === 'low') return <Badge className="bg-green-500">Low</Badge>;
    return <Badge className="bg-gray-500">Unknown</Badge>;
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gray-50 p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => navigate('/admin')}
              className="flex items-center gap-2"
            >
              ← {t('auditDashboard.backToDashboard')}
            </Button>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">{t('auditDashboard.title')}</h1>
              <p className="text-gray-600 mt-1">{t('auditDashboard.subtitle')}</p>
            </div>
          </div>
          
          {/* Date Range Filter */}
          <div className="flex gap-2">
            <Button 
              variant={dateRange === 7 ? "default" : "outline"}
              size="sm"
              onClick={() => setDateRange(7)}
            >
            {t('auditDashboard.days7')}
            </Button>
            <Button 
              variant={dateRange === 30 ? "default" : "outline"}
              size="sm"
              onClick={() => setDateRange(30)}
            >
            {t('auditDashboard.days30')}
            </Button>
            <Button 
              variant={dateRange === 90 ? "default" : "outline"}
              size="sm"
              onClick={() => setDateRange(90)}
            >
            {t('auditDashboard.days90')}
            </Button>
          </div>
        </div>
      </div>
      
      {/* Tabs for Analytics and All Audits */}
      <Tabs defaultValue="analytics" className="space-y-6">
        <TabsList>
          <TabsTrigger value="analytics" className="flex items-center gap-2">
            <BarChart3 className="w-4 h-4" />
            {t('auditDashboard.analytics')}
          </TabsTrigger>
          <TabsTrigger value="all-audits" className="flex items-center gap-2">
            <Table className="w-4 h-4" />
            {t('auditDashboard.allAudits')}
          </TabsTrigger>
        </TabsList>
        
        <TabsContent value="analytics" className="space-y-6">
          {/* Summary Cards */}
          <div className="grid grid-cols-4 gap-6">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">{t('auditDashboard.auditsInRange')}</p>
                <h3 className="text-3xl font-bold mt-2">{summary?.totalAudits || 0}</h3>
                <p className="text-xs text-gray-500 mt-1" data-testid="audits-range-label">
                  {t('auditDashboard.inLastDays', { days: dateRange })}
                </p>
              </div>
              <FileText className="w-10 h-10 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">{t('auditDashboard.highRiskAudits')}</p>
                <h3 className="text-3xl font-bold mt-2 text-red-600">{summary?.highRisk || 0}</h3>
                <p className="text-xs text-gray-500 mt-1" data-testid="high-risk-range-label">
                  {t('auditDashboard.inLastDays', { days: dateRange })}
                </p>
              </div>
              <AlertTriangle className="w-10 h-10 text-red-500" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">{t('auditDashboard.openActions')}</p>
                <h3 className="text-3xl font-bold mt-2 text-orange-600">{summary?.openActions || 0}</h3>
              </div>
              <Target className="w-10 h-10 text-orange-500" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">{t('auditDashboard.companySafetyScore')}</p>
                <h3 className="text-3xl font-bold mt-2 text-green-600">{summary?.companySafetyScore || 0}</h3>
                <p className="text-xs text-gray-500 mt-1">
                  Risk: {summary?.scoreBreakdown?.audit_risk_score} | Actions: {summary?.scoreBreakdown?.action_completion_rate}%
                </p>
              </div>
              <CheckCircle2 className="w-10 h-10 text-green-500" />
            </div>
          </CardContent>
        </Card>
      </div>
      
      {/* Recent Audits + Site Risk */}
      <div className="grid grid-cols-2 gap-6 mb-6">
        {/* Recent Audits Table */}
        <Card>
          <CardHeader>
            <CardTitle>{t('auditDashboard.recentAudits')}</CardTitle>
            <CardDescription>{t('incidents.clickToViewDetails')}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {recentAudits.length > 0 ? (
                recentAudits.map((audit) => (
                  <div
                    key={audit.id}
                    onClick={() => handleAuditClick(audit)}
                    className="p-3 border rounded-lg hover:bg-gray-50 cursor-pointer transition"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <p className="font-semibold">{audit.location}</p>
                          {getRiskBadge(audit.calculatedScoring?.risk_level)}
                        </div>
                        <p className="text-sm text-gray-600 mt-1">
                          {new Date(audit.auditDate).toLocaleDateString()} • {audit.supervisorName}
                        </p>
                      </div>
                      <div className="text-right text-sm text-gray-500">
                        {audit.aiCorrectiveActions?.length || 0} actions
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-gray-500 text-center py-8">No audits found</p>
              )}
            </div>
          </CardContent>
        </Card>
        
        {/* Site Risk Heatmap */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Grid3x3 className="w-5 h-5" />
              {t('auditDashboard.siteRiskByLocation')}
            </CardTitle>
            <CardDescription>{t('auditDashboard.siteRiskDescription')}</CardDescription>
          </CardHeader>
          <CardContent>
            {heatmapData.length > 0 ? (
              <div className="space-y-2">
                {heatmapData.map((row, idx) => (
                  <div key={idx} className="flex items-center gap-3 p-2 rounded hover:bg-gray-50">
                    <div className="w-36 text-sm font-medium truncate">{row.location}</div>
                    <div 
                      className={`w-4 h-4 rounded-full ${
                        row.riskLevel === 'high' ? 'bg-red-500' : 
                        row.riskLevel === 'medium' ? 'bg-yellow-400' : 'bg-green-500'
                      }`}
                      title={`Risk: ${row.riskLevel}`}
                    />
                    <div className="flex-1 text-sm">
                      <span className="text-gray-600">{t('auditDashboard.auditCount', { count: row.auditCount ?? 0 })}</span>
                      <span className="mx-2">•</span>
                      <span className="text-gray-600">Avg: {row.avgScore || 'N/A'}</span>
                    </div>
                    <div className="flex gap-1">
                      {row.highRisk > 0 && <Badge className="bg-red-500 text-xs">{row.highRisk} High</Badge>}
                      {row.mediumRisk > 0 && <Badge className="bg-yellow-500 text-xs">{row.mediumRisk} Med</Badge>}
                      {row.lowRisk > 0 && <Badge className="bg-green-500 text-xs">{row.lowRisk} Low</Badge>}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-8">No heatmap data available. Complete audits at different locations to see risk data.</p>
            )}
          </CardContent>
        </Card>
      </div>
      
      {/* Repeated Hazards + Corrective Actions */}
      <div className="grid grid-cols-2 gap-6 mb-6">
        {/* Repeated Hazards */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-orange-500" />
              Repeated Hazards
            </CardTitle>
            <CardDescription>Click to view details and take action</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 max-h-80 overflow-y-auto">
              {repeatedHazards.length > 0 ? (
                repeatedHazards.map((hazard, idx) => (
                  <div 
                    key={idx} 
                    className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                    onClick={() => openHazardDialog(hazard)}
                  >
                    <div className="flex-1">
                      <p className="text-sm font-medium">{hazard.hazard}</p>
                      <p className="text-xs text-gray-500">{hazard.uniqueLocations || hazard.location_count || 0} location(s)</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="destructive">
                        {hazard.count}x
                      </Badge>
                      <Eye className="w-4 h-4 text-gray-400" />
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-gray-500 text-center py-8">No repeated hazards found</p>
              )}
            </div>
          </CardContent>
        </Card>
        
        {/* Corrective Actions */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-blue-500" />
              {t('correctiveWorkflow.cardTitle')}
            </CardTitle>
            <CardDescription>{t('correctiveWorkflow.cardDescription')}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 max-h-80 overflow-y-auto">
              {correctiveActions.length > 0 ? (
                correctiveActions.slice(0, 10).map((action) => (
                  <div 
                    key={action.id} 
                    className="p-3 border rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                    onClick={() => openActionDialog(action)}
                    data-testid={`ca-row-${action.id}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <p className="text-sm font-medium line-clamp-2">{action.title || action.action}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <p className="text-xs text-gray-500">
                            {action.location && <><MapPin className="w-3 h-3 inline mr-1" />{action.location}</>}
                          </p>
                          {action.dueDate && (
                            <p className="text-xs text-gray-500">
                              <Clock className="w-3 h-3 inline mr-1" />{t('correctiveWorkflow.dueDate')}: {new Date(action.dueDate).toLocaleDateString(i18n.language)}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-1">
                        {action.isFromAudit ? (
                          <Badge variant="outline" className="text-xs" data-testid={`ca-row-suggested-${action.id}`}>
                            {t('correctiveWorkflow.suggested')}
                          </Badge>
                        ) : (
                          <span data-testid={`ca-row-status-${action.id}`}>
                            {getStatusBadge(action.status)}
                          </span>
                        )}
                        {action.isOverdue && (
                          <Badge className="bg-red-100 text-red-800 text-xs" data-testid={`ca-row-overdue-${action.id}`}>
                            {t('correctiveWorkflow.overdue')}
                          </Badge>
                        )}
                      </div>
                    </div>
                    {action.assigneeName && (
                      <p className="text-xs text-blue-600 mt-1">
                        <Users className="w-3 h-3 inline mr-1" />{t('correctiveWorkflow.assignee')}: {action.assigneeName}
                      </p>
                    )}
                  </div>
                ))
              ) : (
                <p className="text-gray-500 text-center py-8">{t('correctiveWorkflow.noActions')}</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
      
      {flags.phase3CorrectiveActions && (
        <div className="mb-6">
          <CorrectiveActionMetrics scope="company" refreshKey={metricsRefresh} />
        </div>
      )}

      {/* Supervisor Performance + JSA Integration */}
      <div className="grid grid-cols-2 gap-6 mb-6">
        {/* Supervisor Performance */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="w-5 h-5" />
              Supervisor Performance
            </CardTitle>
            <CardDescription>Audit activity and safety scores</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {supervisorPerformance.length > 0 ? (
                supervisorPerformance.map((sup, idx) => (
                  <div key={idx} className="p-3 border rounded-lg">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-semibold">{sup.supervisor}</p>
                        <p className="text-sm text-gray-600">
                          {sup.audits_conducted} audits • Avg compliance: {sup.avg_compliance_rate ?? 'n/a'}%
                        </p>
                      </div>
                      <div className="flex gap-1">
                        {sup.high_risk_count > 0 && (
                          <Badge className="bg-red-500">{sup.high_risk_count} High</Badge>
                        )}
                        {sup.medium_risk_count > 0 && (
                          <Badge className="bg-yellow-500">{sup.medium_risk_count} Med</Badge>
                        )}
                        {sup.low_risk_count > 0 && (
                          <Badge className="bg-green-500">{sup.low_risk_count} Low</Badge>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-gray-500 text-center py-8">No supervisor data available</p>
              )}
            </div>
          </CardContent>
        </Card>
        
        {/* JSA Integration */}
        <Card>
          <CardHeader>
            <CardTitle>JSA Integration</CardTitle>
            <CardDescription>Audits with JSA recommendations</CardDescription>
          </CardHeader>
          <CardContent>
            {jsaIntegration ? (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <p className="text-2xl font-bold">{jsaIntegration.audits_with_jsa_suggestions}</p>
                  <BarChart3 className="w-8 h-8 text-blue-500" />
                </div>
                <p className="text-sm text-gray-600 mb-4">Audits with JSA update suggestions</p>
                
                {jsaIntegration.recent_suggestions?.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-xs font-semibold text-gray-700">Recent Suggestions:</p>
                    {jsaIntegration.recent_suggestions.slice(0, 3).map((item) => (
                      <div key={item.id} className="text-xs p-2 bg-gray-50 rounded">
                        <p className="font-medium">{item.location}</p>
                        <p className="text-gray-600">{item.aiJsaUpdates?.length || 0} update(s)</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-8">Loading JSA data...</p>
            )}
          </CardContent>
        </Card>
      </div>
      
      {/* AI Recommendations */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-purple-500" />
            AI Recommendations for Your Company
          </CardTitle>
          <CardDescription>Click to acknowledge, create task, or dismiss</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4">
            {aiRecommendations.length > 0 ? (
              aiRecommendations.map((rec, idx) => (
                <div 
                  key={idx} 
                  className={`p-4 border rounded-lg cursor-pointer transition-all hover:shadow-md ${
                    rec.status === 'acknowledged' ? 'bg-blue-50 border-blue-200' :
                    rec.status === 'task_created' ? 'bg-green-50 border-green-200' :
                    rec.status === 'dismissed' ? 'bg-gray-50 border-gray-200 opacity-60' :
                    'hover:bg-gray-50'
                  }`}
                  onClick={() => {
                    setSelectedRec(rec);
                    setShowRecDialog(true);
                  }}
                >
                  <div className="flex items-start justify-between mb-2">
                    <h4 className="font-semibold text-sm">{rec.title}</h4>
                    <div className="flex gap-1">
                      {getPriorityBadge(rec.priority)}
                      {rec.status === 'acknowledged' && <Badge className="bg-blue-500">Seen</Badge>}
                      {rec.status === 'task_created' && <Badge className="bg-green-500">Tasked</Badge>}
                      {rec.status === 'dismissed' && <Badge variant="outline">Dismissed</Badge>}
                    </div>
                  </div>
                  <p className="text-sm text-gray-600">{rec.reason}</p>
                  {rec.locations && rec.locations.length > 0 && (
                    <p className="text-xs text-gray-500 mt-2">
                      <MapPin className="w-3 h-3 inline mr-1" />
                      {rec.locations.slice(0, 3).join(', ')}{rec.locations.length > 3 ? '...' : ''}
                    </p>
                  )}
                </div>
              ))
            ) : (
              <p className="text-gray-500 text-center py-8 col-span-2">No recommendations at this time. Complete more audits to receive AI insights.</p>
            )}
          </div>
        </CardContent>
      </Card>
      
      {/* Reports & Exports */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Download className="w-5 h-5" />
            Reports & Exports
          </CardTitle>
          <CardDescription>Download audit reports and data</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-4">
            {/* Monthly Audit Report */}
            <div>
              <p className="text-sm font-medium mb-2">Monthly Audit Report</p>
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={async () => {
                    try {
                      toast.info(t('adminToasts.GeneratingCsv'));
                      const response = await axiosInstance.get(`/company/audits/export/monthly?days=${dateRange}&format=csv`, {
                        responseType: 'blob'
                      });
                      const url = window.URL.createObjectURL(new Blob([response.data]));
                      const link = document.createElement('a');
                      link.href = url;
                      link.setAttribute('download', `monthly_audit_report_${new Date().toISOString().split('T')[0]}.csv`);
                      document.body.appendChild(link);
                      link.click();
                      link.remove();
                      window.URL.revokeObjectURL(url);
                      toast.success(t('adminToasts.CsvDownloaded'));
                    } catch (error) {
                      console.error('CSV download error:', error);
                      toast.error(`Failed to download CSV: ${error.message}`);
                    }
                  }}
                  className="flex-1"
                >
                  <Download className="w-3 h-3 mr-1" />
                  CSV
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={async () => {
                    try {
                      toast.info(t('adminToasts.GeneratingPdf'));
                      const response = await axiosInstance.get(`/company/audits/export/monthly?days=${dateRange}&format=pdf`, {
                        responseType: 'blob'
                      });
                      const url = window.URL.createObjectURL(new Blob([response.data]));
                      const link = document.createElement('a');
                      link.href = url;
                      link.setAttribute('download', `monthly_audit_report_${new Date().toISOString().split('T')[0]}.pdf`);
                      document.body.appendChild(link);
                      link.click();
                      link.remove();
                      window.URL.revokeObjectURL(url);
                      toast.success(t('adminToasts.PdfDownloaded'));
                    } catch (error) {
                      console.error('PDF download error:', error);
                      toast.error(`Failed to download PDF: ${error.message}`);
                    }
                  }}
                  className="flex-1"
                >
                  <FileText className="w-3 h-3 mr-1" />
                  PDF
                </Button>
              </div>
            </div>

            {/* Hazard Trends */}
            <div>
              <p className="text-sm font-medium mb-2">Hazard Trends</p>
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={async () => {
                    try {
                      toast.info(t('adminToasts.GeneratingCsv'));
                      const response = await axiosInstance.get(`/company/audits/export/hazards?days=${dateRange}&format=csv`, {
                        responseType: 'blob'
                      });
                      const url = window.URL.createObjectURL(new Blob([response.data]));
                      const link = document.createElement('a');
                      link.href = url;
                      link.setAttribute('download', `hazard_trends_${new Date().toISOString().split('T')[0]}.csv`);
                      document.body.appendChild(link);
                      link.click();
                      link.remove();
                      toast.success(t('adminToasts.CsvDownloaded'));
                    } catch (error) {
                      toast.error(t('adminToasts.FailedToDownloadCsv'));
                    }
                  }}
                  className="flex-1"
                >
                  <Download className="w-3 h-3 mr-1" />
                  CSV
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={async () => {
                    try {
                      toast.info(t('adminToasts.GeneratingPdf'));
                      const response = await axiosInstance.get(`/company/audits/export/hazards?days=${dateRange}&format=pdf`, {
                        responseType: 'blob'
                      });
                      const url = window.URL.createObjectURL(new Blob([response.data]));
                      const link = document.createElement('a');
                      link.href = url;
                      link.setAttribute('download', `hazard_trends_${new Date().toISOString().split('T')[0]}.pdf`);
                      document.body.appendChild(link);
                      link.click();
                      link.remove();
                      toast.success(t('adminToasts.PdfDownloaded'));
                    } catch (error) {
                      toast.error(t('adminToasts.FailedToDownloadPdf'));
                    }
                  }}
                  className="flex-1"
                >
                  <FileText className="w-3 h-3 mr-1" />
                  PDF
                </Button>
              </div>
            </div>

            {/* Corrective Actions */}
            <div>
              <p className="text-sm font-medium mb-2">Corrective Actions</p>
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={async () => {
                    try {
                      toast.info(t('adminToasts.GeneratingCsv'));
                      const response = await axiosInstance.get(`/company/audits/export/corrective-actions?days=${dateRange}&format=csv`, {
                        responseType: 'blob'
                      });
                      const url = window.URL.createObjectURL(new Blob([response.data]));
                      const link = document.createElement('a');
                      link.href = url;
                      link.setAttribute('download', `corrective_actions_${new Date().toISOString().split('T')[0]}.csv`);
                      document.body.appendChild(link);
                      link.click();
                      link.remove();
                      toast.success(t('adminToasts.CsvDownloaded'));
                    } catch (error) {
                      toast.error(t('adminToasts.FailedToDownloadCsv'));
                    }
                  }}
                  className="flex-1"
                >
                  <Download className="w-3 h-3 mr-1" />
                  CSV
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={async () => {
                    try {
                      toast.info(t('adminToasts.GeneratingPdf'));
                      const response = await axiosInstance.get(`/company/audits/export/corrective-actions?days=${dateRange}&format=pdf`, {
                        responseType: 'blob'
                      });
                      const url = window.URL.createObjectURL(new Blob([response.data]));
                      const link = document.createElement('a');
                      link.href = url;
                      link.setAttribute('download', `corrective_actions_${new Date().toISOString().split('T')[0]}.pdf`);
                      document.body.appendChild(link);
                      link.click();
                      link.remove();
                      toast.success(t('adminToasts.PdfDownloaded'));
                    } catch (error) {
                      toast.error(t('adminToasts.FailedToDownloadPdf'));
                    }
                  }}
                  className="flex-1"
                >
                  <FileText className="w-3 h-3 mr-1" />
                  PDF
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
        </TabsContent>
        
        <TabsContent value="all-audits">
          <AllAuditsTable user={user} />
        </TabsContent>
      </Tabs>
      
      {/* Audit Details Dialog — full-featured view with photos, AI analysis, policy violations */}
      <ViewAuditDialog
        user={user}
        audit={selectedAudit}
        open={showAuditDrawer}
        onClose={() => setShowAuditDrawer(false)}
        onUpdate={(updated) => setSelectedAudit(updated)}
        onDelete={() => {
          setShowAuditDrawer(false);
          setSelectedAudit(null);
          loadDashboardData();
        }}
      />
      
      {/* Corrective Action Management Dialog */}
      <Dialog open={showActionDialog} onOpenChange={setShowActionDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{t('auditDashboard.manageAction')}</DialogTitle>
            <DialogDescription>
              {t('correctiveWorkflow.cardDescription')}
            </DialogDescription>
          </DialogHeader>
          
          {selectedAction && (
            <div className="space-y-4">
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="font-medium">{selectedAction.title || selectedAction.action}</p>
                {selectedAction.location && (
                  <p className="text-sm text-gray-500 mt-1">
                    <MapPin className="w-3 h-3 inline mr-1" />{selectedAction.location}
                  </p>
                )}
                {selectedAction.isFromAudit && (
                  <Badge className="mt-2 bg-blue-100 text-blue-800">{t('correctiveWorkflow.fromAuditBadge')}</Badge>
                )}
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>{t('correctiveWorkflow.status')}</Label>
                  <Select
                    value={actionUpdate.status}
                    disabled={['pending_verification', 'closed'].includes(selectedAction.status)}
                    onValueChange={(v) => setActionUpdate(prev => ({...prev, status: v}))}
                  >
                    <SelectTrigger data-testid="action-status-select">
                      <SelectValue placeholder={t('correctiveWorkflow.status')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="open">{t('correctiveWorkflow.statusOpen')}</SelectItem>
                      <SelectItem value="assigned">{t('correctiveWorkflow.statusAssigned')}</SelectItem>
                      <SelectItem value="in_progress">{t('correctiveWorkflow.statusInProgress')}</SelectItem>
                      <SelectItem value="dismissed">{t('correctiveWorkflow.statusDismissed')}</SelectItem>
                      {['pending_verification', 'closed'].includes(selectedAction.status) && (
                        <SelectItem value={selectedAction.status}>
                          {t(`correctiveWorkflow.${selectedAction.status === 'closed' ? 'statusClosed' : 'statusPendingVerification'}`)}
                        </SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label>{t('correctiveWorkflow.priority')}</Label>
                  <Select value={actionUpdate.priority} onValueChange={(v) => setActionUpdate(prev => ({...prev, priority: v}))}>
                    <SelectTrigger>
                      <SelectValue placeholder={t('correctiveWorkflow.priority')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="High">{t('correctiveWorkflow.priorityHigh')}</SelectItem>
                      <SelectItem value="Medium">{t('correctiveWorkflow.priorityMedium')}</SelectItem>
                      <SelectItem value="Low">{t('correctiveWorkflow.priorityLow')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div>
                <Label>{t('correctiveWorkflow.assignee')}</Label>
                <Select value={actionUpdate.assignedTo || '__unassigned__'} onValueChange={(v) => setActionUpdate(prev => ({...prev, assignedTo: v === '__unassigned__' ? '' : v}))}>
                  <SelectTrigger data-testid="action-assignee-select">
                    <SelectValue placeholder={t('correctiveWorkflow.assignee')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__unassigned__">{t('correctiveWorkflow.unassigned')}</SelectItem>
                    {assignees.map((user) => (
                      <SelectItem key={user.id} value={user.id}>
                        {user.name} ({user.role})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>{t('correctiveWorkflow.dueDate')}</Label>
                <Input 
                  type="date" 
                  value={actionUpdate.dueDate} 
                  onChange={(e) => setActionUpdate(prev => ({...prev, dueDate: e.target.value}))}
                />
              </div>
              
              <div>
                <Label>{t('correctiveWorkflow.addNote')}</Label>
                <Textarea 
                  placeholder={t('correctiveWorkflow.notePlaceholder')}
                  value={actionUpdate.notes}
                  onChange={(e) => setActionUpdate(prev => ({...prev, notes: e.target.value}))}
                  rows={3}
                />
              </div>
              
              {!selectedAction.isFromAudit && flags.phase3CorrectiveActions && (
                <CorrectiveActionWorkflow
                  action={selectedAction}
                  currentUserId={user?.id}
                  canVerify={['company_admin', 'division_admin', 'super_admin', 'supervisor'].includes(user?.role)}
                  onUpdated={async (updated) => {
                    let fresh = updated;
                    if (!fresh) {
                      const { data } = await axiosInstance.get(`/company/corrective-actions/${selectedAction.id}`);
                      fresh = data;
                    }
                    setSelectedAction(fresh);
                    setActionUpdate(prev => ({
                      ...prev,
                      status: fresh.status || prev.status,
                      assignedTo: fresh.assignedTo || '',
                      dueDate: fresh.dueDate || '',
                      priority: fresh.priority || prev.priority,
                      notes: ''
                    }));
                    loadDashboardData();
                  }}
                />
              )}
              
              {/* Show history if available */}
              {selectedAction.history && selectedAction.history.length > 0 && (
                <div>
                  <Label className="mb-2 block">{t('correctiveWorkflow.history')}</Label>
                  <div className="max-h-48 overflow-y-auto space-y-2">
                    {[...selectedAction.history].reverse().map((h, idx) => (
                      <div key={idx} className="text-xs p-2 bg-gray-50 rounded">
                        <span className="font-medium">{h.by}</span>{' '}
                        {t(`correctiveWorkflow.historyAction.${h.action}`, { defaultValue: h.action })}
                        {h.details ? ` — ${h.details}` : ''}
                        <span className="text-gray-400 ml-2">{new Date(h.timestamp).toLocaleDateString(i18n.language)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowActionDialog(false)} data-testid="action-cancel-btn">
              {t('correctiveWorkflow.cancel')}
            </Button>
            <Button onClick={saveActionUpdate} disabled={savingAction} data-testid="action-save-btn">
              {savingAction ? t('correctiveWorkflow.saving') : t('correctiveWorkflow.saveChanges')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Hazard Details Dialog */}
      <Dialog open={showHazardDialog} onOpenChange={setShowHazardDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-orange-500" />
              Repeated Hazard Details
            </DialogTitle>
            <DialogDescription>
              View occurrences and take action
            </DialogDescription>
          </DialogHeader>
          
          {selectedHazard && (
            <div className="space-y-4">
              <div className="p-4 bg-orange-50 border border-orange-200 rounded-lg">
                <p className="font-semibold text-lg">{selectedHazard.hazard}</p>
                <p className="text-sm text-gray-600 mt-1">
                  Detected <strong>{selectedHazard.count}</strong> times across <strong>{selectedHazard.uniqueLocations || selectedHazard.locations?.length || 0}</strong> location(s)
                </p>
              </div>
              
              {loadingHazard ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600 mx-auto"></div>
                  <p className="text-gray-500 mt-2">Loading details...</p>
                </div>
              ) : hazardDetails ? (
                <div>
                  <Label className="mb-2 block">Audits with this hazard ({hazardDetails.audits?.length || 0})</Label>
                  <div className="max-h-48 overflow-y-auto space-y-2">
                    {hazardDetails.audits?.map((audit, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded">
                        <div>
                          <p className="font-medium">{audit.location}</p>
                          <p className="text-xs text-gray-500">
                            {audit.date} • {audit.supervisor}
                          </p>
                        </div>
                        {audit.riskScore && (
                          <Badge className={
                            audit.riskScore.toLowerCase() === 'high' ? 'bg-red-500' :
                            audit.riskScore.toLowerCase() === 'medium' ? 'bg-yellow-500' : 'bg-green-500'
                          }>
                            {audit.riskScore}
                          </Badge>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}
            </div>
          )}
          
          <DialogFooter className="flex gap-2">
            <Button variant="outline" onClick={() => setShowHazardDialog(false)}>Close</Button>
            <Button 
              variant="outline" 
              onClick={() => createActionFromHazard(false)}
              className="border-orange-300 text-orange-700 hover:bg-orange-50"
            >
              <Plus className="w-4 h-4 mr-1" />
              Create Company Action
            </Button>
            <Button 
              onClick={() => createActionFromHazard(true)}
              className="bg-orange-600 hover:bg-orange-700"
            >
              <Bell className="w-4 h-4 mr-1" />
              Create Action & Notify Supervisors
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* AI Recommendation Action Dialog */}
      <Dialog open={showRecDialog} onOpenChange={setShowRecDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-purple-500" />
              AI Recommendation
            </DialogTitle>
            <DialogDescription>
              Take action on this recommendation
            </DialogDescription>
          </DialogHeader>
          
          {selectedRec && (
            <div className="space-y-4">
              <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg">
                <p className="font-semibold">{selectedRec.title}</p>
                <p className="text-sm text-gray-600 mt-2">{selectedRec.reason}</p>
                {selectedRec.locations && selectedRec.locations.length > 0 && (
                  <div className="mt-3">
                    <p className="text-xs font-semibold text-gray-700">Affected Locations:</p>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {selectedRec.locations.map((loc, idx) => (
                        <Badge key={idx} variant="outline" className="text-xs">{loc}</Badge>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              
              {selectedRec.status && selectedRec.status !== 'new' && (
                <div className="p-3 bg-gray-50 rounded">
                  <p className="text-sm">
                    <strong>Current Status:</strong> {selectedRec.status === 'acknowledged' ? 'Acknowledged' : selectedRec.status === 'task_created' ? 'Task Created' : 'Dismissed'}
                  </p>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter className="flex gap-2">
            <Button variant="outline" onClick={() => setShowRecDialog(false)}>Cancel</Button>
            <Button 
              variant="outline" 
              onClick={() => handleRecAction('dismiss')}
              disabled={actioningRec || selectedRec?.status === 'dismissed'}
            >
              <X className="w-4 h-4 mr-1" />
              Dismiss
            </Button>
            <Button 
              variant="outline"
              onClick={() => handleRecAction('acknowledge')}
              disabled={actioningRec || selectedRec?.status === 'acknowledged'}
              className="border-blue-300 text-blue-700 hover:bg-blue-50"
            >
              <Check className="w-4 h-4 mr-1" />
              Acknowledge
            </Button>
            <Button 
              onClick={() => handleRecAction('create_task')}
              disabled={actioningRec || selectedRec?.status === 'task_created'}
              className="bg-purple-600 hover:bg-purple-700"
            >
              <Plus className="w-4 h-4 mr-1" />
              Create Task
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CompanyAuditDashboard;
